import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Pencil,
  RotateCcw,
  Trash2,
  GripVertical,
  Save,
  X,
  Sparkles,
  Loader2,
} from "lucide-react";
import { useEventTools } from "../hooks/useEventTools";
import {
  createEventTool,
  updateEventTool,
  deleteEventTool,
  reorderEventTools,
  resetEventTool,
} from "../lib/api";
import {
  TOOL_ICONS,
  TOOL_ICON_NAMES,
  TOOL_ACCENTS,
  ACCENT_CLASSES,
} from "../lib/toolDefaults";
import type { EventTool, ToolAccent } from "../lib/types";

interface Props {
  eventId: string;
}

type CadenceMode = "manual" | "once" | "repeated";
type DraftState = "creating" | "editing";

interface Draft {
  state: DraftState;
  id: string | null;
  tool_key: string;
  toolKeyEdited: boolean;
  label: string;
  icon: string;
  accent: ToolAccent;
  scope: "question" | "event";
  cadenceMode: CadenceMode;
  cadenceFirst: string;
  cadenceStep: string;
  prompt: string;
}

const NEW_TOOL_PROMPT_PLACEHOLDER = `AUFGABE: Beschreibe, was die KI aus den Antworten herausarbeiten soll.

HARTE EINSCHRÄNKUNGEN:
- Nur auf Basis der Antworten, nichts erfinden.

OUTPUT-FORMAT:
- Ein bis drei Sätze, Deutsch, ohne Vorwort.`;

function slugifyToolKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function cadenceModeFor(tool: EventTool): CadenceMode {
  if (tool.cadence_first === null) return "manual";
  if (tool.cadence_step === null) return "once";
  return "repeated";
}

function cadenceSubtitle(tool: EventTool): string {
  const scopePrefix = tool.scope === "event" ? "Ganzes Event · " : "";
  if (tool.cadence_first === null) return `${scopePrefix}Nur manuell`;
  if (tool.cadence_step === null)
    return `${scopePrefix}Einmalig bei ${tool.cadence_first} Antworten`;
  return `${scopePrefix}Erstmals bei ${tool.cadence_first}, dann alle +${tool.cadence_step}`;
}

function draftFromTool(tool: EventTool): Draft {
  return {
    state: "editing",
    id: tool.id,
    tool_key: tool.tool_key,
    toolKeyEdited: true,
    label: tool.label,
    icon: tool.icon,
    accent: tool.accent,
    scope: tool.scope ?? "question",
    cadenceMode: cadenceModeFor(tool),
    cadenceFirst: tool.cadence_first !== null ? String(tool.cadence_first) : "10",
    cadenceStep: tool.cadence_step !== null ? String(tool.cadence_step) : "20",
    prompt: tool.prompt,
  };
}

function newDraft(): Draft {
  return {
    state: "creating",
    id: null,
    tool_key: "",
    toolKeyEdited: false,
    label: "",
    icon: "Sparkles",
    accent: "berry",
    scope: "question",
    cadenceMode: "repeated",
    cadenceFirst: "10",
    cadenceStep: "20",
    prompt: NEW_TOOL_PROMPT_PLACEHOLDER,
  };
}

export default function ToolsEditor({ eventId }: Props) {
  const { tools, loading } = useEventTools(eventId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [resettingId, setResettingId] = useState<string | null>(null);

  // Close the open editor if the tool it was editing has just been deleted
  // remotely (realtime can change `tools` under us).
  useEffect(() => {
    if (draft?.state === "editing" && draft.id) {
      if (!tools.some((t) => t.id === draft.id)) {
        setDraft(null);
      }
    }
  }, [tools, draft]);

  const editingTool = useMemo(() => {
    if (draft?.state !== "editing" || !draft.id) return null;
    return tools.find((t) => t.id === draft.id) ?? null;
  }, [draft, tools]);

  const toggleEnabled = async (tool: EventTool) => {
    try {
      await updateEventTool(tool.id, { is_enabled: !tool.is_enabled });
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Konnte Tool nicht aktualisieren.");
    }
  };

  const handleReset = async (tool: EventTool) => {
    setResettingId(tool.id);
    setRowError(null);
    try {
      await resetEventTool(tool);
      if (draft?.id === tool.id) {
        // Reflect the reset values in any open editor for this row.
        // The hook will refresh `tools` shortly; close the editor for clarity.
        setDraft(null);
      }
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Zurücksetzen fehlgeschlagen.");
    } finally {
      setResettingId(null);
    }
  };

  const handleDelete = async (tool: EventTool) => {
    if (tool.is_builtin) return;
    if (!window.confirm("Tool löschen?")) return;
    try {
      await deleteEventTool(tool.id);
      if (draft?.id === tool.id) setDraft(null);
    } catch (e) {
      setRowError(e instanceof Error ? e.message : "Löschen fehlgeschlagen.");
    }
  };

  const startEdit = (tool: EventTool) => {
    setFormError(null);
    setRowError(null);
    setDraft(draftFromTool(tool));
  };

  const startCreate = () => {
    setFormError(null);
    setRowError(null);
    setDraft(newDraft());
  };

  const cancelDraft = () => {
    setDraft(null);
    setFormError(null);
  };

  const updateDraft = (patch: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const onLabelChange = (label: string) => {
    setDraft((d) => {
      if (!d) return d;
      const next = { ...d, label };
      if (d.state === "creating" && !d.toolKeyEdited) {
        next.tool_key = slugifyToolKey(label);
      }
      return next;
    });
  };

  const onToolKeyChange = (raw: string) => {
    const cleaned = raw.toLowerCase().replace(/[^a-z0-9_]+/g, "_").slice(0, 60);
    setDraft((d) => (d ? { ...d, tool_key: cleaned, toolKeyEdited: true } : d));
  };

  const validateDraft = (d: Draft): string | null => {
    const trimmedLabel = d.label.trim();
    if (trimmedLabel.length < 1 || trimmedLabel.length > 60) {
      return "Label muss 1 bis 60 Zeichen lang sein.";
    }
    if (d.state === "creating") {
      if (!d.tool_key || d.tool_key.length < 1) {
        return "Tool-Key darf nicht leer sein.";
      }
    }
    if (d.cadenceMode === "once" || d.cadenceMode === "repeated") {
      const first = parseInt(d.cadenceFirst, 10);
      if (!Number.isFinite(first) || first < 1) {
        return "Ab wie vielen Antworten? Bitte eine Zahl ≥ 1.";
      }
    }
    if (d.cadenceMode === "repeated") {
      const step = parseInt(d.cadenceStep, 10);
      if (!Number.isFinite(step) || step < 1) {
        return "Schrittweite muss eine Zahl ≥ 1 sein.";
      }
    }
    if (d.prompt.trim().length < 1) {
      return "Prompt darf nicht leer sein.";
    }
    return null;
  };

  const handleSave = async () => {
    if (!draft) return;
    const error = validateDraft(draft);
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setSaving(true);
    const firstNum = parseInt(draft.cadenceFirst, 10);
    const stepNum = parseInt(draft.cadenceStep, 10);
    // Event-wide tools run manual-only (mirrors the workshop-ai server guard).
    const cadence_first = draft.scope === "event" || draft.cadenceMode === "manual" ? null : firstNum;
    const cadence_step = draft.scope === "event" || draft.cadenceMode !== "repeated" ? null : stepNum;
    try {
      if (draft.state === "creating") {
        await createEventTool(eventId, {
          tool_key: draft.tool_key,
          label: draft.label.trim(),
          icon: draft.icon,
          accent: draft.accent,
          prompt: draft.prompt,
          scope: draft.scope,
          cadence_first,
          cadence_step,
          sort_order: tools.length,
        });
      } else if (draft.id) {
        await updateEventTool(draft.id, {
          label: draft.label.trim(),
          icon: draft.icon,
          accent: draft.accent,
          prompt: draft.prompt,
          scope: draft.scope,
          cadence_first,
          cadence_step,
        });
      }
      setDraft(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/duplicate|unique/i.test(msg)) {
        setFormError("Ein Tool mit diesem Tool-Key existiert bereits in diesem Event.");
      } else {
        setFormError(msg || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetFromForm = async () => {
    if (!editingTool || !editingTool.is_builtin) return;
    setSaving(true);
    setFormError(null);
    try {
      await resetEventTool(editingTool);
      setDraft(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Zurücksetzen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (id: string) => (e: React.DragEvent<HTMLButtonElement>) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Required for some browsers to actually initiate the drag.
    e.dataTransfer.setData("text/plain", id);
  };

  const onDragOverRow = (id: string) => (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };

  const onDragLeaveRow = (id: string) => () => {
    if (dragOverId === id) setDragOverId(null);
  };

  const onDropRow = (targetId: string) => async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const orderedIds = tools.map((t) => t.id);
    const fromIdx = orderedIds.indexOf(sourceId);
    const toIdx = orderedIds.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    orderedIds.splice(fromIdx, 1);
    orderedIds.splice(toIdx, 0, sourceId);
    setRowError(null);
    try {
      await reorderEventTools(orderedIds);
    } catch (err) {
      setRowError(err instanceof Error ? err.message : "Reihenfolge konnte nicht gespeichert werden.");
    }
  };

  const onDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-rc-black font-bold text-[18px] tracking-[-0.01em]">
            KI-Tools · fürs ganze Event
          </h3>
          <p className="text-[12px] text-rc-slate mt-1 leading-[1.5]">
            Diese Tools gelten für das gesamte Event. Jedes Tool läuft standardmäßig gegen die aktive Frage
            (oder – bei Datenbasis „Ganzes Event“ – gegen alle Fragen).
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          disabled={draft?.state === "creating"}
          className="inline-flex items-center gap-1.5 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold text-[13px] rounded-pill px-4 py-2 transition-colors active:scale-[0.97]"
        >
          <Plus className="w-3.5 h-3.5" />
          Tool hinzufügen
        </button>
      </header>

      {rowError && (
        <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-rc-lg px-4 py-2.5 text-rc-danger text-[13px] font-medium">
          {rowError}
        </div>
      )}

      {loading && tools.length === 0 ? (
        <p className="text-[13px] text-rc-slate italic">Lade Tools…</p>
      ) : tools.length === 0 && draft?.state !== "creating" ? (
        <p className="text-[13px] text-rc-slate italic">
          Noch keine Tools. Mit „Tool hinzufügen" das erste anlegen.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tools.map((tool) => {
            const Icon = TOOL_ICONS[tool.icon] ?? Sparkles;
            const accentClasses = ACCENT_CLASSES[tool.accent];
            const isEditing = draft?.state === "editing" && draft.id === tool.id;
            const isDragOver = dragOverId === tool.id && dragId !== tool.id;
            return (
              <li key={tool.id}>
                <div
                  onDragOver={onDragOverRow(tool.id)}
                  onDragLeave={onDragLeaveRow(tool.id)}
                  onDrop={onDropRow(tool.id)}
                  className={`bg-rc-paper border rounded-rc-lg transition-colors ${
                    isDragOver ? "border-rc-berry" : "border-rc-fog"
                  } ${dragId === tool.id ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-3 px-3 py-3">
                    <button
                      type="button"
                      draggable
                      onDragStart={onDragStart(tool.id)}
                      onDragEnd={onDragEnd}
                      aria-label="Verschieben"
                      title="Ziehen, um Reihenfolge zu ändern"
                      className="text-rc-slate hover:text-rc-black cursor-grab active:cursor-grabbing transition-colors p-1 -ml-1"
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <span
                      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accentClasses.swatch}`}
                    >
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[14px] font-semibold text-rc-black truncate">
                          {tool.label}
                        </p>
                        {tool.is_builtin && (
                          <span className="text-[9.5px] font-semibold text-rc-slate uppercase tracking-[0.14em] bg-rc-cream border border-rc-fog rounded-pill px-1.5 py-0.5">
                            Standard
                          </span>
                        )}
                      </div>
                      <p className="text-[12px] text-rc-slate truncate">
                        {cadenceSubtitle(tool)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={tool.is_enabled}
                        aria-label={tool.is_enabled ? "Tool deaktivieren" : "Tool aktivieren"}
                        onClick={() => toggleEnabled(tool)}
                        className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                          tool.is_enabled ? "bg-rc-berry" : "bg-rc-fog"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-rc-paper shadow transition-transform ${
                            tool.is_enabled ? "translate-x-4" : "translate-x-0"
                          }`}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => (isEditing ? cancelDraft() : startEdit(tool))}
                        aria-label={isEditing ? "Bearbeitung schließen" : "Tool bearbeiten"}
                        title={isEditing ? "Bearbeitung schließen" : "Bearbeiten"}
                        className={`p-2 rounded-full transition-colors ${
                          isEditing
                            ? "text-rc-berry bg-rc-berry/10"
                            : "text-rc-charcoal hover:text-rc-black hover:bg-rc-cream"
                        }`}
                      >
                        {isEditing ? <X className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                      </button>
                      {tool.is_builtin ? (
                        <button
                          type="button"
                          onClick={() => handleReset(tool)}
                          disabled={resettingId === tool.id}
                          aria-label="Auf Standard zurücksetzen"
                          title="Auf Standard zurücksetzen"
                          className="p-2 rounded-full text-rc-charcoal hover:text-rc-black hover:bg-rc-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {resettingId === tool.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDelete(tool)}
                          aria-label="Tool löschen"
                          title="Tool löschen"
                          className="p-2 rounded-full text-rc-charcoal hover:text-rc-danger hover:bg-rc-danger/8 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {isEditing && draft && (
                    <div className="border-t border-rc-fog px-4 py-4">
                      <ToolForm
                        draft={draft}
                        editingTool={editingTool}
                        onLabelChange={onLabelChange}
                        onToolKeyChange={onToolKeyChange}
                        onChange={updateDraft}
                        onSave={handleSave}
                        onCancel={cancelDraft}
                        onReset={handleResetFromForm}
                        saving={saving}
                        error={formError}
                      />
                    </div>
                  )}
                </div>
              </li>
            );
          })}

          {draft?.state === "creating" && (
            <li>
              <div className="bg-rc-paper border border-rc-berry rounded-rc-lg p-4">
                <p className="text-[11px] font-semibold text-rc-berry uppercase tracking-[0.14em] mb-3">
                  Neues Tool
                </p>
                <ToolForm
                  draft={draft}
                  editingTool={null}
                  onLabelChange={onLabelChange}
                  onToolKeyChange={onToolKeyChange}
                  onChange={updateDraft}
                  onSave={handleSave}
                  onCancel={cancelDraft}
                  onReset={handleResetFromForm}
                  saving={saving}
                  error={formError}
                />
              </div>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

// ─── Edit form ────────────────────────────────────────────────────────────

interface ToolFormProps {
  draft: Draft;
  editingTool: EventTool | null;
  onLabelChange: (label: string) => void;
  onToolKeyChange: (raw: string) => void;
  onChange: (patch: Partial<Draft>) => void;
  onSave: () => void;
  onCancel: () => void;
  onReset: () => void;
  saving: boolean;
  error: string | null;
}

const CADENCE_OPTIONS: Array<{ key: CadenceMode; label: string; sub: string }> = [
  { key: "manual", label: "Manuell", sub: "Nur per Klick" },
  { key: "once", label: "Einmalig", sub: "Einmal bei N Antworten" },
  { key: "repeated", label: "Wiederholt", sub: "Bei N, dann alle +M" },
];

function ToolForm({
  draft,
  editingTool,
  onLabelChange,
  onToolKeyChange,
  onChange,
  onSave,
  onCancel,
  onReset,
  saving,
  error,
}: ToolFormProps) {
  const isBuiltin = editingTool?.is_builtin === true;
  const showToolKeyField = draft.state === "creating";
  const inputClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-2.5 text-[14px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors";

  return (
    <div className="flex flex-col gap-4">
      <div>
        <label
          htmlFor="tool-label"
          className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2"
        >
          Label
        </label>
        <input
          id="tool-label"
          type="text"
          value={draft.label}
          onChange={(e) => onLabelChange(e.target.value)}
          maxLength={60}
          placeholder="z. B. Stimmungsbarometer"
          className={inputClass}
        />
      </div>

      {showToolKeyField && (
        <div>
          <label
            htmlFor="tool-key"
            className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2"
          >
            Tool-Key
          </label>
          <input
            id="tool-key"
            type="text"
            value={draft.tool_key}
            onChange={(e) => onToolKeyChange(e.target.value)}
            placeholder="stimmungsbarometer"
            className={`${inputClass} font-mono text-[13px]`}
          />
          <p className="text-[11px] text-rc-slate mt-1.5 leading-[1.5]">
            Eindeutige Kennung innerhalb dieses Events. Nur Kleinbuchstaben, Ziffern und Unterstrich.
          </p>
        </div>
      )}

      {!showToolKeyField && isBuiltin && (
        <div>
          <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] mb-1">
            Tool-Key
          </p>
          <p className="text-[13px] text-rc-slate font-mono">{draft.tool_key}</p>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2">
          Icon
        </p>
        <div className="flex flex-wrap gap-1.5">
          {TOOL_ICON_NAMES.map((name) => {
            const Icon = TOOL_ICONS[name];
            const selected = draft.icon === name;
            return (
              <button
                key={name}
                type="button"
                onClick={() => onChange({ icon: name })}
                aria-label={name}
                aria-pressed={selected}
                title={name}
                className={`w-9 h-9 rounded-lg flex items-center justify-center border-[1.5px] transition-colors ${
                  selected
                    ? "bg-rc-berry/10 border-rc-berry text-rc-berry ring-2 ring-rc-berry/20"
                    : "bg-rc-paper border-rc-fog text-rc-charcoal hover:border-rc-charcoal/40 hover:text-rc-black"
                }`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2">
          Akzentfarbe
        </p>
        <div className="flex gap-2">
          {TOOL_ACCENTS.map((accent) => {
            const selected = draft.accent === accent;
            return (
              <button
                key={accent}
                type="button"
                onClick={() => onChange({ accent })}
                aria-label={accent}
                aria-pressed={selected}
                title={accent}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform ${
                  ACCENT_CLASSES[accent].swatch
                } ${selected ? "ring-2 ring-rc-berry ring-offset-2 ring-offset-rc-paper" : "hover:scale-105"}`}
              >
                <span className="sr-only">{accent}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2">
          Datenbasis
        </p>
        <div className="grid grid-cols-2 gap-2">
          {([
            { key: "question", label: "Aktuelle Frage", sub: "Antworten der aktiven Frage" },
            { key: "event", label: "Ganzes Event", sub: "Alle Fragen und Antworten" },
          ] as const).map((opt) => {
            const selected = draft.scope === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() =>
                  onChange(
                    opt.key === "event"
                      ? { scope: "event", cadenceMode: "manual" }
                      : { scope: "question" },
                  )
                }
                aria-pressed={selected}
                className={`text-left px-3 py-2.5 rounded-xl text-[13px] font-semibold border-[1.5px] transition-colors ${
                  selected
                    ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
                    : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/40"
                }`}
              >
                <span className="block">{opt.label}</span>
                <span className="block text-[11px] font-medium text-rc-slate mt-0.5">
                  {opt.sub}
                </span>
              </button>
            );
          })}
        </div>
        {draft.scope === "event" && (
          <p className="text-[11px] text-rc-slate mt-2 leading-[1.5]">
            Event-weite Tools laufen nur manuell und werten alle Fragen und Antworten des Events aus.
          </p>
        )}
      </div>

      {draft.scope === "question" && (
      <div>
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2">
          Auslöser
        </p>
        <div className="grid grid-cols-3 gap-2">
          {CADENCE_OPTIONS.map((opt) => {
            const selected = draft.cadenceMode === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChange({ cadenceMode: opt.key })}
                aria-pressed={selected}
                className={`text-left px-3 py-2.5 rounded-xl text-[13px] font-semibold border-[1.5px] transition-colors ${
                  selected
                    ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
                    : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/40"
                }`}
              >
                <span className="block">{opt.label}</span>
                <span className="block text-[11px] font-medium text-rc-slate mt-0.5">
                  {opt.sub}
                </span>
              </button>
            );
          })}
        </div>
        {(draft.cadenceMode === "once" || draft.cadenceMode === "repeated") && (
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <label className="flex items-center gap-2 text-[13px] text-rc-charcoal">
              Erstmals bei
              <input
                type="number"
                min="1"
                max="9999"
                value={draft.cadenceFirst}
                onChange={(e) => onChange({ cadenceFirst: e.target.value })}
                className="w-20 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
              />
              Antworten
            </label>
            {draft.cadenceMode === "repeated" && (
              <label className="flex items-center gap-2 text-[13px] text-rc-charcoal">
                dann alle
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={draft.cadenceStep}
                  onChange={(e) => onChange({ cadenceStep: e.target.value })}
                  className="w-20 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
                weitere
              </label>
            )}
          </div>
        )}
      </div>
      )}

      <div>
        <label
          htmlFor="tool-prompt"
          className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2"
        >
          Prompt
        </label>
        <textarea
          id="tool-prompt"
          value={draft.prompt}
          onChange={(e) => onChange({ prompt: e.target.value })}
          rows={12}
          className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[12.5px] text-rc-black font-mono placeholder:text-rc-slate/70 resize-y focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors leading-[1.55]"
        />
        <p className="text-[11px] text-rc-slate mt-1.5 leading-[1.5]">
          Die Antworten, Cluster und der Event-Kontext werden automatisch oberhalb deines Prompts mitgesendet.
          Die KI antwortet mit reinem Text; ein JSON-Format musst du nicht vorgeben.
        </p>
      </div>

      {error && (
        <div className="bg-rc-danger/8 border border-rc-danger/30 rounded-rc-lg px-4 py-2.5 text-rc-danger text-[13px] font-medium">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold text-[13px] rounded-pill px-4 py-2 transition-colors active:scale-[0.97]"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-rc-charcoal hover:text-rc-black font-semibold text-[13px] rounded-pill border-[1.5px] border-rc-fog hover:border-rc-charcoal/40 px-4 py-[7px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Abbrechen
        </button>
        {isBuiltin && (
          <button
            type="button"
            onClick={onReset}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-rc-charcoal hover:text-rc-black font-semibold text-[13px] rounded-pill border-[1.5px] border-rc-fog hover:border-rc-charcoal/40 px-4 py-[7px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Auf Standard zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
}
